/** A tappable NxN square-grid board, shared by the board games (checkers; chess and battleship next). */
import { colNumber, displayCells, displayCellsTopDown, fileLabel, rankLabel, rowLetter, type DisplayCell } from '@campfire/grid';
import { h } from '../dom.ts';

export interface CellSpec {
  /** Extra classes, space separated. */
  readonly classes?: string;
  readonly content?: Node | null;
  /** Makes the cell a button. Without it the cell is inert. */
  readonly onTap?: () => void;
  readonly label?: string;
  readonly attrs?: Record<string, string | number>;
}

export interface GridBoardOptions {
  readonly size: number;
  /** Half-turn the board so the second player has their own side at the bottom. */
  readonly flip: boolean;
  readonly testid: string;
  /** Returns null for a square that is not part of play (drawn as an inert backdrop square). */
  readonly cell: (c: DisplayCell) => CellSpec | null;
  /** Draw file letters along the bottom edge and rank numbers down the left edge. */
  readonly coords?: boolean;
  /** Chart style (battleship): row A at the top, columns numbered along the top edge, rows lettered down the left. Ignores `flip`. */
  readonly topDown?: boolean;
}

export function gridBoard(o: GridBoardOptions): HTMLElement {
  const cells = o.topDown ? displayCellsTopDown(o.size) : displayCells(o.size, o.flip);
  const kids = cells.map((c) => {
    const spec = o.cell(c);
    const coord = o.coords && o.topDown
      ? [
          c.col === 0 ? h('span.gb-coord.rank', {}, rowLetter(c.y)) : null,
          c.row === 0 ? h('span.gb-coord.col', {}, colNumber(c.x)) : null,
        ]
      : o.coords
      ? [
          c.col === 0 ? h('span.gb-coord.rank', {}, rankLabel(c.y)) : null,
          c.row === o.size - 1 ? h('span.gb-coord.file', {}, fileLabel(c.x)) : null,
        ]
      : [];
    if (spec === null) return h('div.gb-cell.void', {}, coord);
    const attrs = { 'aria-label': spec.label, ...spec.attrs, ...(spec.onTap ? { onclick: spec.onTap } : { 'aria-disabled': 'true' }) };
    return h(`${spec.onTap ? 'button' : 'div'}.gb-cell${spec.classes ? `.${spec.classes.split(' ').join('.')}` : ''}`, attrs, spec.content, coord);
  });
  return h('div.gb-board', { 'data-testid': o.testid, style: `--gb-n:${o.size}` }, kids);
}
